/* eslint-disable prettier/prettier */
import React, { ReactElement, useRef, useState } from "react";
import { FilePreview, FileUpload, Label, Spinner, Text, TextArea, } from "@xometry/xometry_loft";
import { useContextSelector } from "use-context-selector";

import { useMutation } from "@apollo/client";
import { OrderFulfillmentTerms, UpsertOrderFulfillmentTermsQuery, } from "../../../schema/generated/schema";
import UserContext from "../contexts/userContext";

import Modal from "../Common/Modal";
import { FileShareEntityType } from "../../../utils/fileUtils";
import ProposalContext from "../contexts/proposalContext";
import { modalFiles } from "./FulfilledModal.styles";
import FileContext from "../contexts/fileContext";

const SubmissionModal = ({
  modalOpen,
  toggleModal,
  onSubmit,
  orderId,
  fulfillmentTerms,
}: {
  modalOpen: boolean;
  toggleModal: (boolean) => void;
  // eslint-disable-next-line @typescript-eslint/ban-types
  onSubmit?: Function;
  orderId: string | undefined;
  fulfillmentTerms?: OrderFulfillmentTerms | null;
}): ReactElement => {
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState<any[]>([]);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const addFiles = useContextSelector(FileContext, ctx => ctx.actions.addFiles);
  const loggedInUserId = useContextSelector(UserContext, ctx => ctx.loggedInUser?.id);
  const proposal = useContextSelector(
    ProposalContext,
    context => context.data?.getProposal?.proposal
  );
  const { sellerPartnerId } = proposal ?? {};

  const [upsertOrderFulfillmentTerms] = useMutation(
    UpsertOrderFulfillmentTermsQuery
  );

  const handleUpsert = () => upsertOrderFulfillmentTerms({
    variables: {
      orderId,
      item: {
        shippingNotes: inputRef.current?.value || ""
      },
    },
  });

  const submitForm = async () => {
    try {
      await handleUpsert();
      if (onSubmit) {
        onSubmit();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleDrop = async accepted => {
    setUploading(true);
    const newFiles: File[] = accepted.map(file => {
      return file.clientFile;
    });
    if (!orderId) {
      throw new Error("order id not found");
    }
    try {
      await addFiles(
        newFiles,
        { type: FileShareEntityType.MARKETPLACE_ORDER, id: orderId },
        loggedInUserId || "",
        proposal?.buyerUserId || "",
        sellerPartnerId || "",
      );
    } catch (err) {
      console.error(err);
    }
    setUploading(false);
  };

  return (
    <Modal
      isOpen={modalOpen}
      selector="#modal-mount"
      title={<Text kind="panel-title"> Mark as Fulfilled </Text>}
      description={
        <Text>
          Clicking Mark as Fulfilled will schedule your payout and inform your
          customer that their order will be shipped soon/has been shipped.
        </Text>
      }
      submitActionText="Mark As Fulfilled"
      handleCloseButton={() => {
        toggleModal(false);
      }}
      handleCancel={() => {
        toggleModal(false);
      }}
      handleSubmit={() => {
        submitForm();
      }}
      buttonScale="large"
    >
      <>
        <Label>Shipping Details</Label>
        <TextArea
          ref={inputRef}
          scale="large"
          name="shippingNotes"
          placeholder="Enter any relevant shipping details, e.g. tracking numbers..."
          defaultValue={fulfillmentTerms?.shippingNotes || ""}
        />
      </>
      <div css={modalFiles}>
        <Label>Additional Files</Label>
        {uploading ? (
          <Spinner scale="panel" />
        ) : (
          <FileUpload
            fileList={fileList}
            fileLimit={0}
            sizeLimit={104857600}
            suppressErrorDisplay={false}
            accept={[]}
            onFileSelected={selectedFiles => {
              handleDrop(selectedFiles);
              return setFileList([...fileList, ...selectedFiles]);
            }}
          />
        )}
        <>
          {fileList.map(file => {
            return (
              <FilePreview
                key={`${file.clientFile.id}`}
                clientFile={file.clientFile}
                onFileRemove={(idx, selectedFile) => {
                  const newFileList = fileList.filter(
                    fileInList =>
                      fileInList.clientFile.name !== selectedFile.name
                  );
                  return setFileList(newFileList);
                }}
              />
            );
          })}
        </>
      </div>
    </Modal>
  );
};

SubmissionModal.defaultProps = {
  onSubmit: undefined,
  fulfillmentTerms: {
    shippingNotes: "",
  },
};

export default SubmissionModal;
